'use client';
import { setUtmSourceCookie } from '#pplx/frontend/apps/ask/browser-state/cookieManager.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { addToUserCohort } from '#pplx/frontend/apps/ask/queries/upsell-queries/addToUserCohort.js';
import { AMEX_USER_COHORT } from '#pplx/frontend/apps/ask/queries/upsell-queries/constants.js';
import { replaceHistorySearchParams } from '#pplx/frontend/apps/ask/routing/historySearchParams.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { MAX_PAYWALL_PARAM, UNINSTALL_EXTENSION_PARAM, UTM_SOURCE_PARAM, } from '#pplx/frontend/components/utils/consts.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
const PARTNER_PARAM = 'partner';
const PARTNER_COHORTS = {
    amex: AMEX_USER_COHORT,
};
// Single-file event: defined here at its only call site rather than in a shared package.
const defaultSearchExtensionUninstalledEvent = EVENT_PAYLOAD;
export const useHomepageQueryParamsEffect = () => {
    const query = useSearchParams();
    const { $t } = useIntl();
    const openPaywallUpsell = usePaywallUpsell();
    const { device: { userAgent }, } = useWebPlatform();
    const isUserLoggedIn = useIsUserLoggedIn();
    const { track } = useInstrumentation();
    const { openVisitorLoginImageUpsell } = useBrandedVisitorGate({
        enabled: true,
    });
    const hasAttemptedPartnerCohortEnrollment = useRef(false);
    // handle and swallow url params
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const utmSource = query?.get(UTM_SOURCE_PARAM);
        const swallowParams = [];
        if (query?.get(UNINSTALL_EXTENSION_PARAM)) {
            swallowParams.push(UNINSTALL_EXTENSION_PARAM);
            track(defaultSearchExtensionUninstalledEvent({
                name: 'default search extension uninstalled',
                data: {
                    isBrowserExtension: true,
                },
            }));
        }
        if (query?.get(MAX_PAYWALL_PARAM)) {
            swallowParams.push(MAX_PAYWALL_PARAM);
            openPaywallUpsell({
                origin: ModalOrigin.MAX_URL_PAYWALL,
                pitchMessage: {
                    title: $t({
                        defaultMessage: 'Introducing Perplexity Max',
                        description: 'Title for the Perplexity Max paywall upsell triggered by URL parameter',
                        id: '896589f2bf',
                    }),
                    description: $t({
                        defaultMessage: 'Maximize your curiosity with enhanced access to Perplexity file and app creation',
                        description: 'Description for the Perplexity Max paywall upsell triggered by URL parameter',
                        id: 'd18cf95db5',
                    }),
                },
            });
        }
        if (utmSource) {
            setUtmSourceCookie(utmSource);
        }
        const partner = query?.get(PARTNER_PARAM);
        const cohort = partner && Object.hasOwn(PARTNER_COHORTS, partner) ? PARTNER_COHORTS[partner] : undefined;
        if (isUserLoggedIn && cohort && !hasAttemptedPartnerCohortEnrollment.current) {
            hasAttemptedPartnerCohortEnrollment.current = true;
            void addToUserCohort(cohort);
        }
        if (swallowParams.length > 0) {
            const remainingParams = Array.from(query?.entries() ?? []).filter(([key]) => !swallowParams.includes(key));
            replaceHistorySearchParams(Object.fromEntries(remainingParams));
        }
    }, [query, openPaywallUpsell, userAgent, isUserLoggedIn, $t, track, openVisitorLoginImageUpsell]);
};
//# sourceMappingURL=useHomepageQueryParamsEffect.js.map